import { FC, useEffect, useState } from 'react';
import { EntityData, ServerDetails, UserDetails } from '../types';
import { Button, ButtonStyles, EmptyStateIcons, Search, Table, Tabs } from '@silverfort/sf-components';
import moment from 'moment';
import IdentityDataActivityTab from './IdentityDataActivityTab';
import { getUserState, isUserPermitted } from '../../utils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { User, UserSession } from '../../../api/types/UserData';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import IdentityDataRiskTab from './IdentityDataRiskTab';
import { usePricingModulesState } from '../../../context/contexts/PricingModuleContext';

const createPropertyRow = (title, data) => {
    if (data === undefined || !title) {
        return <></>;
    }

    return (
        <div className="flex w-full items-center">
            <div className="h-4 w-[200px] text-sf-gray-800 font-medium text-13">{title}</div>
            <div className="h-4 text-sf-gray-500 text-13">{data || 'N/A'}</div>
        </div>
    );
};

const renderPropertiesTab = (entityData: EntityData, adType: number, onEntityClick) => {
    if (adType === AD_ENTITY_TYPES.USER) {
        const details = entityData.details as UserDetails;
        return (
            <div className="border h-full w-full border-sf-gray-100 py-4 px-6 rounded">
                <div className="flex h-full flex-col gap-4">
                    {createPropertyRow('User Principal Name', details.identifier)}
                    {createPropertyRow('Domain', details.domain_name)}
                    {createPropertyRow('SAM Account', details.user_name)}
                    {createPropertyRow('Email', details.email)}
                    {createPropertyRow('OU', details.path?.split('\\')[0])}
                    {createPropertyRow(
                        'MFA Enrollment',
                        entityData.tokensCount ? `Yes, has ${entityData.tokensCount} tokens` : 'No'
                    )}
                    {createPropertyRow(
                        'Primary User',
                        entityData.parentGuidData?.parentGuid ? (
                            <span
                                onClick={() =>
                                    onEntityClick(
                                        entityData.parentGuidData?.parentGuid,
                                        entityData.parentGuidData?.parentDisplayName,
                                        AD_ENTITY_TYPES.USER
                                    )
                                }
                                className="text-sf-primary-500 cursor-pointer">
                                {entityData.parentGuidData.parentDisplayName}
                            </span>
                        ) : undefined
                    )}
                    {createPropertyRow('Guid', details.guid)}
                    {createPropertyRow(
                        'Password Last Changed',
                        details.pwd_last_set ? moment(details.pwd_last_set * 1000).format('MMM DD, YYYY, HH:mm') : null
                    )}
                </div>
            </div>
        );
    }

    if (adType === AD_ENTITY_TYPES.SERVER || adType === AD_ENTITY_TYPES.HOST) {
        const details = entityData.details as ServerDetails;
        return (
            <div className="border h-full w-full border-sf-gray-100 py-2 px-6 rounded">
                <div className="flex h-full flex-col gap-4">
                    {createPropertyRow('Operating System', details.operating_system)}
                    {createPropertyRow('Domain', details.domain_name)}
                    {createPropertyRow('Entity Guid', details.guid)}
                    {createPropertyRow('Active Policy', entityData.rules?.length ? 'Yes' : 'No')}
                    {createPropertyRow('Services', details.services?.length || 0)}
                </div>
            </div>
        );
    }
};

const renderTabData = (entityData, selectedTab, adType, onEntityClick, tableSearch, setTableSearch, user) => {
    switch (selectedTab) {
        case 0:
            return renderPropertiesTab(entityData, adType, onEntityClick);
        case 1:
            return <IdentityDataActivityTab onEntityClick={onEntityClick} entityData={entityData} adType={adType} />;
        case 2:
            return (
                <>
                    <div className="flex items-center justify-end">
                        <Search placeholder="Search group" border text={tableSearch} onChange={setTableSearch} />
                    </div>
                    <Table
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            { columnKey: 'sid', title: 'SID' }
                        ]}
                        onRowClick={(item: { guid: string; name: string; sid: string }) =>
                            onEntityClick(item.guid, item.name, AD_ENTITY_TYPES.GROUP)
                        }
                        rows={
                            entityData.groups?.filter((group) =>
                                group.name.toLowerCase()?.includes(tableSearch?.toLowerCase())
                            ) || []
                        }
                        EmptyStateProps={{ title: 'No Groups Memberships', icon: EmptyStateIcons.NoItems }}
                    />
                </>
            );
        case 3:
            return (
                <>
                    <div className="flex items-center justify-end">
                        <Search placeholder="Search AAD Roles" border text={tableSearch} onChange={setTableSearch} />
                    </div>
                    <Table
                        disableHighlightedFirstColumn
                        rowClassName="!cursor-default"
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            { columnKey: 'description', title: 'Description' },
                            {
                                columnKey: 'is_built_in',
                                title: 'Built in',
                                render: (row) => {
                                    return row.is_built_in ? 'Built in' : '';
                                }
                            }
                        ]}
                        rows={
                            entityData.azureRoles?.filter((role) =>
                                role.name.toLowerCase().includes(tableSearch?.toLowerCase())
                            ) || []
                        }
                        EmptyStateProps={{ title: 'No Entra ID Roles', icon: EmptyStateIcons.NoItems }}
                    />
                </>
            );
        case 4:
            return (
                <>
                    <div className="flex items-center justify-end">
                        <Search placeholder="Search policy" border text={tableSearch} onChange={setTableSearch} />
                    </div>
                    <Table
                        disableHighlightedFirstColumn
                        rowClassName="!cursor-default"
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            {
                                columnKey: 'id',
                                title: '',
                                render: (row) => {
                                    return isUserPermitted(user, 'policies', ROLES.VIEWER.value) ? (
                                        <div className="flex items-center justify-end">
                                            <a
                                                id={'manage_identity_manage_policy_' + row.id}
                                                href={`/Policy#${row.id}`}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                className="decoration-transparent !cursor-pointer"
                                                onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    text="Manage policy"
                                                    onClick={() => {}}
                                                    buttonStyle={ButtonStyles.TertiaryBorder}
                                                />
                                            </a>
                                        </div>
                                    ) : (
                                        <></>
                                    );
                                }
                            }
                        ]}
                        rows={
                            entityData.rules?.filter((rule) =>
                                rule.name.toLowerCase().includes(tableSearch?.toLowerCase())
                            ) || []
                        }
                        EmptyStateProps={{ title: 'No Active Policies', icon: EmptyStateIcons.NoItems }}
                    />
                </>
            );
        case 5: {
            return <IdentityDataRiskTab entityData={entityData} />;
        }
        default:
            break;
    }
};

const calcRisksCount = (riskReasons, pricingModulesState) => {
    let totalCount = riskReasons?.filter((risk) => risk.type).length || 0;

    if (!pricingModulesState?.ispm?.anyEnabled) {
        const exposures = riskReasons.filter((risk) => risk.type === 'Exposure');
        totalCount -= exposures.length;
    }

    if (!pricingModulesState?.itdr?.anyEnabled) {
        const incidents = riskReasons.filter((risk) => risk.type === 'Incident');
        totalCount -= incidents.length;
    }

    return totalCount;
};

const IdentityDataTable: FC<{
    entityData: EntityData;
    adType: number;
    onEntityClick: (guid?: string, identifier?: string, type?: number) => void;
}> = ({ entityData, adType, onEntityClick }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [tableSearch, setTableSearch] = useState('');
    const [user, setUser] = useState<User | undefined>();
    const { pricingModulesState } = usePricingModulesState();

    useEffect(() => {
        getUserState().then((userData) => {
            if (userData) setUser((userData as UserSession).user);
        });
    }, []);

    useEffect(() => {
        setTableSearch('');
    }, [selectedTab]);

    const IDENTITY_DATA_TABS = [
        { value: 0, name: 'Properties' },
        { value: 1, name: 'Activity' },
        { value: 2, name: `Groups (${entityData.groups?.length || 0})` },
        { value: 3, name: 'Entra ID roles', isDisabled: adType !== AD_ENTITY_TYPES.USER },
        { value: 4, name: `Active policies (${entityData.rules?.length || 0})` },
        { value: 5, name: `Security risks (${calcRisksCount(entityData.RiskReasons, pricingModulesState)})` }
    ];

    return (
        <div className="h-full font-sf w-full flex flex-col gap-4 px-6 py-4">
            <Tabs
                selectedTab={selectedTab}
                onClick={setSelectedTab}
                tabs={IDENTITY_DATA_TABS.filter((tab) => {
                    if (tab.value === 5) {
                        return pricingModulesState?.ispm?.anyEnabled || pricingModulesState?.itdr?.anyEnabled;
                    }
                    return true;
                })}
            />
            {renderTabData(entityData, selectedTab, adType, onEntityClick, tableSearch, setTableSearch, user)}
        </div>
    );
};

export default IdentityDataTable;
